import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessions } from '../../hooks/useSessions';
import SessionRow from './SessionRow';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';
import type { SessionFilterState } from '../../pages/SessionsPage';
import { resolveIdentityQuery, type ResolvedIdentity } from '../../utils/identity';

interface SessionListProps {
  filters: SessionFilterState;
  page: number;
  onPageChange: (page: number) => void;
}

export default function SessionList({ filters, page, onPageChange }: SessionListProps) {
  const [, setSearchParams] = useSearchParams();
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [identity, setIdentity] = useState<ResolvedIdentity | null>(null);
  const identityQuery = filters.identityQuery || '';

  // Load watched sessions from localStorage
  useEffect(() => {
    const watched = JSON.parse(localStorage.getItem('vantage_watched_sessions') || '[]');
    setWatchedIds(new Set(watched));
  }, []);

  // Sync page number to URL params
  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', String(page));
      return newParams;
    }, { replace: true });
  }, [page, setSearchParams]);

  // Debounced resolution of the typed identity query → hash
  useEffect(() => {
    let cancelled = false;
    if (!identityQuery.trim()) {
      setIdentity(null);
      return;
    }
    const handle = setTimeout(() => {
      resolveIdentityQuery(identityQuery).then((r) => {
        if (!cancelled) setIdentity(r);
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [identityQuery]);

  const identityActive = !!identityQuery.trim();
  const identityResolving = identityActive && !identity;
  const identityInvalid = identity?.kind === 'invalid';
  // Skip the query while we're mid-hash to avoid briefly showing unfiltered results
  const queryEnabled = !identityActive || (!!identity && identity.kind !== 'invalid');

  const { data, isLoading } = useSessions({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    dateRange: filters.dateRange,
    device: filters.device === 'all' ? undefined : filters.device,
    hasErrors: filters.hasErrors || undefined,
    hasRageClicks: filters.hasRageClicks || undefined,
    url: filters.url || undefined,
    customerId: identity?.kind === 'customerId' ? identity.value : undefined,
    emailHash: identity?.kind === 'emailHash' ? identity.value : undefined,
    phoneHash: identity?.kind === 'phoneHash' ? identity.value : undefined,
    enabled: queryEnabled,
  });

  if (isLoading) {
    return (
      <div className="card flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const sessions = data?.data || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-4">
      {/* Identity search status banner */}
      {identityActive && (
        <div
          className={
            identityInvalid
              ? 'flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'
              : 'flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800'
          }
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {identityResolving && <span>Hashing query…</span>}
          {identityInvalid && (
            <span>
              "{identityQuery}" isn't a recognisable email, phone, or customer ID — try a different format.
            </span>
          )}
          {!identityResolving && !identityInvalid && identity && (
            <span>
              Filtered to sessions matching this {identity.label}. Clear "Find by user" to see all sessions again.
            </span>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="table-header">Visitor</th>
              <th className="table-header">Date</th>
              <th className="table-header">Duration</th>
              <th className="table-header">Pages</th>
              <th className="table-header">Entry Page</th>
              <th className="table-header">Device</th>
              <th className="table-header">Country</th>
              <th className="table-header">Tags</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                isWatched={watchedIds.has(session.id)}
              />
            ))}
            {sessions.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                  No sessions found matching your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * DEFAULT_PAGE_SIZE + 1} to{' '}
            {Math.min(page * DEFAULT_PAGE_SIZE, data?.total || 0)} of{' '}
            {(data?.total || 0).toLocaleString()} sessions
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                    p === page
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
